import React from "react";

/**
 * SegmentedPills — Composant de référence unique pour :
 * - vues (Jour / Semaine / Mois, Liste / Kanban, Calendrier / Planning)
 * - filtres de statuts (Toutes / Confirmées / …)
 * - filtres temporels (Jour / Semaine / Mois / Année)
 * - filtres de portée (Tous / Par véhicule / Par catégorie)
 *
 * Design : conteneur neutre, pill actif blanc + ombre, pill inactif texte slate.
 *
 * Props :
 *   - items: [{ key, label, count?, icon? }]
 *   - value: string
 *   - onChange: (key) => void
 *   - size: 'sm' (default 32px) | 'md' (36px)
 *   - testid: optional prefix for data-testid on each pill
 */
export default function SegmentedPills({
  items,
  value,
  onChange,
  size = "sm",
  testid,
  className = "",
  variant = "segmented",
}) {
  const h = size === "md" ? "h-9" : "h-8";
  const px = size === "md" ? "px-3.5" : "px-3";
  const text = "text-xs font-medium";
  const isPills = variant === "pills";

  return (
    <div
      role="tablist"
      className={
        isPills
          ? `flex flex-wrap items-center gap-2 ${className}`
          : `inline-flex items-center gap-0.5 p-1 rounded-xl bg-slate-100 ${className}`
      }
      data-testid={testid}
    >
      {items.map((it) => {
        const active = it.key === value;
        const Icon = it.icon;
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.key)}
            data-testid={testid ? `${testid}-${it.key}` : undefined}
            className={
              isPills
                ? `inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border ${text} whitespace-nowrap transition-colors ${
                    active
                      ? "border-violet-300 bg-violet-50 text-violet-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`
                : `inline-flex items-center gap-1.5 ${h} ${px} rounded-lg ${text} whitespace-nowrap transition-colors ${
                    active
                      ? "bg-white shadow-sm text-slate-900"
                      : "text-slate-500 hover:text-slate-700"
                  }`
            }
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            <span>{it.label}</span>
            {typeof it.count === "number" && (
              <span
                className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold tabular-nums ${
                  active && !isPills
                    ? "bg-slate-100 text-slate-700"
                    : "bg-slate-200/70 text-slate-500"
                }`}
              >
                {it.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
